import { useEffect, useMemo } from 'react';
import { useGridPageClick } from './useGridPageClick';

export type UseGridPaginationParams = {
    page: number;
    perPage: number;
    total: number;
    onPageChange: (page: number) => void;
};

export type GridPaginationNumbersRowProps = {
    selectedPage: number;
    maxPage: number;
    onPageClick: (page: number) => void;
};

const getMaxPage = (perPage: number, total: number) => (perPage > 0 ? Math.ceil(total / perPage) : 0);

/**
 * Пагинация списка на гриде: расчёт `maxPage`, скролл к началу при смене страницы и `paginationProps`.
 *
 * @kind helper
 */
export function useGridPagination({ page, perPage, total, onPageChange }: UseGridPaginationParams) {
    const { gridRef, handlerPageClick } = useGridPageClick(onPageChange);

    const maxPage = useMemo(() => getMaxPage(perPage, total), [perPage, total]);

    useEffect(() => {
        if (maxPage > 0 && page > maxPage) {
            onPageChange(maxPage);
        }
    }, [maxPage, onPageChange, page]);

    const paginationProps = useMemo<GridPaginationNumbersRowProps>(() => {
        return {
            selectedPage: page,
            maxPage,
            onPageClick: handlerPageClick,
        };
    }, [page, maxPage, handlerPageClick]);

    const isPaginationVisible = maxPage > 1;

    return { gridRef, maxPage, paginationProps, isPaginationVisible };
}
